import axios from 'axios';
import { getWidth as getExtentWidth } from 'ol/extent';
import MVT from 'ol/format/MVT';
import Group from 'ol/layer/Group';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorSource from 'ol/source/Vector';
import VectorTileSource from 'ol/source/VectorTile';
import WMTSSource from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { getValidatedParcelStyle, highlightStyle } from './styles';
import { proj3857 } from './proj';

const PARCEL_MIN_ZOOM = 14.9;

const ENV_LAYERS = [
  {
    name: 'ZNIEFF 1',
    typeName: 'PROTECTEDAREAS.ZNIEFF1',
  },
  {
    name: 'ZNIEFF 2',
    typeName: 'PROTECTEDAREAS.ZNIEFF2',
  },
  {
    name: 'Sites Natura 2000 (Directive Oiseaux)',
    typeName: 'PROTECTEDAREAS.ZPS',
  },
  {
    name: 'Sites Natura 2000 (Directive Habitats)',
    typeName: 'PROTECTEDAREAS.SIC',
  },
  {
    name: 'Réserves naturelles',
    typeNames: ['PROTECTEDAREAS.RN', 'PROTECTEDSITES.MNHN.RESERVES-REGIONALES', 'PROTECTEDAREAS.RNC'],
  },
  {
    name: "Zones humides d'importance internationale (sites RAMSAR)",
    typeName: 'PROTECTEDAREAS.RAMSAR',
  },
  {
    name: 'Parcs nationaux',
    typeName: 'PROTECTEDAREAS.PN',
  },
  {
    name: 'Parcs naturels régionaux',
    typeName: 'PROTECTEDAREAS.PNR',
  },
  {
    name: 'Arrêtés de protection de biotope',
    typeName: 'PROTECTEDAREAS.APB',
  },
];

const RESOLUTIONS = [],
  MATRIX_IDS = [];
const MAX_RESOLUTION = getExtentWidth(proj3857.getExtent()) / 256;

for (let i = 0; i < 20; i++) {
  MATRIX_IDS[i] = i.toString();
  RESOLUTIONS[i] = MAX_RESOLUTION / Math.pow(2, i);
}

const parcelLayerSource = new VectorTileSource({
  format: new MVT(),
  url: `${import.meta.env.VITE_CADASTRE_API_URL}/parcelles-mvt?z={z}&x={x}&y={y}`,
  attributions: '© Données cadastrales Etalab',
  tileLoadFunction: (tile, url) => {
    tile.setLoader((extent, resolution, projection) => {
      axios
        .get(url, {
          headers: {
            'ns-api-key': import.meta.env.VITE_CADASTRE_API_KEY,
          },
          responseType: 'arraybuffer',
        })
        .then(({ data }) => {
          const format = tile.getFormat();
          const features = format.readFeatures(data, {
            extent,
            featureProjection: projection,
          });
          tile.setFeatures(features);
        });
    });
  },
});

export const parcelLayer = new VectorTileLayer({
  source: parcelLayerSource,
  minZoom: PARCEL_MIN_ZOOM,
  visible: true,
  opacity: 0.7,
  properties: {
    name: 'Parcelles cadastrales',
  },
});

export const selectedParcelLayer = new VectorTileLayer({
  renderMode: 'vector',
  source: parcelLayerSource,
  minZoom: PARCEL_MIN_ZOOM,
});

export const validatedParcelLayer = new VectorLayer({
  source: new VectorSource(),
  style: (feature) => getValidatedParcelStyle(feature),
});

const getIgnSource = (layer, format, style) => {
  return new WMTSSource({
    crossOrigin: '*',
    layer,
    url: 'https://data.geopf.fr/wmts',
    matrixSet: 'PM',
    format: `image/${format}`,
    projection: proj3857,
    tileGrid: new WMTSTileGrid({
      origin: [-20037508, 20037508],
      resolutions: RESOLUTIONS,
      matrixIds: MATRIX_IDS,
    }),
    style,
    attributions: '<a href="http://www.geoportail.fr/" target="_blank">© IGN Géoportail</a>',
  });
};

export const baseLayers = [
  new TileLayer({
    source: getIgnSource('ORTHOIMAGERY.ORTHOPHOTOS', 'jpeg', 'normal'),
    visible: true,
    properties: {
      name: 'Photos aériennes',
    },
  }),
  new TileLayer({
    source: getIgnSource('ORTHOIMAGERY.ORTHOPHOTOS.IRC', 'jpeg', 'normal'),
    visible: false,
    properties: {
      name: 'Photos aériennes IRC',
    },
  }),
  new TileLayer({
    source: getIgnSource('GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2', 'png', 'normal'),
    visible: false,
    properties: {
      name: 'Plan IGN',
    },
  }),
  parcelLayer,
];

const getEnvLayer = (typeName, style, opts = {}) =>
  new TileLayer({
    source: getIgnSource(typeName, 'png', typeName),
    ...opts,
  });

export const envLayers = ENV_LAYERS.map((layerInfo) => {
  const layerOpts = {
    properties: { name: layerInfo.name },
    opacity: 0.7,
    visible: false,
  };

  if (layerInfo.typeNames) {
    return new Group({
      ...layerOpts,
      layers: layerInfo.typeNames.map((typeName) => getEnvLayer(typeName)),
    });
  }

  return getEnvLayer(layerInfo.typeName, layerInfo.style, layerOpts);
});

export const measureDrawLayer = new VectorLayer({
  source: new VectorSource(),
  style: highlightStyle,
  visible: true,
  opacity: 0.7,
});
